import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TreeNode } from 'primeng/api';
import { PermissionService } from '../../../services/permission.service';
import { DashboardHelper } from '../../../services/dashboard.service';
import { CHATBOT_AUTO_CLOSE_ROUTES } from '../../../modules/catch-core/data/constants/route.constants';
import * as PermissionConstants from '../../../modules/catch-core/data/constants/permission.constants';

@Component({
  selector: 'app-settings-tab',
  templateUrl: './settings-tab.component.html',
  styleUrls: ['./settings-tab.component.css'],
  standalone: false,
})
export class SettingsTabComponent implements OnInit, OnDestroy {
  routeLinks: any[] = [];
  activeLinkIndex = -1;

  constructor(
    private router: Router,
    private dashboradHelper: DashboardHelper,
    private permissionService: PermissionService,
  ) {
    if (this.permissionService.hasPermission(PermissionConstants.ORG_READ)) {
      this.routeLinks = [
        {
          label: 'Organisation',
          link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.ORGS_TAB] } }],
          index: 0,
        },
      ];
    }

    if (this.permissionService.isSysAdmin) {
      this.routeLinks.push({
        label: 'Directories',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.DIRECTORIES] } }],
        index: 1,
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.PERMISSION_READ)) {
      this.routeLinks.push({
        label: 'Permissions',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.PERMISSION_TAB] } }],
        index: 2,
        category: 'User Management',
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.ROLE_READ)) {
      this.routeLinks.push({
        label: 'Roles',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.ROLES_TAB] } }],
        index: 3,
        category: 'User Management',
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.GROUP_READ)) {
      this.routeLinks.push({
        label: 'Groups',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.GROUPS_TAB] } }],
        index: 4,
        category: 'User Management',
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.USER_READ)) {
      this.routeLinks.push({
        label: 'Users',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.USERS_TAB] } }],
        index: 5,
        category: 'User Management',
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.LICENSE_READ)) {
      this.routeLinks.push({
        label: 'License',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.LICENSE_TAB] } }],
        index: 6,
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.EXTERNAL_LINK_CREATE)) {
      this.routeLinks.push({
        label: 'Apps',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.EXTERNAL_LINKS_TAB] } }],
        index: 7,
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.APIKEY_READ)) {
      this.routeLinks.push({
        label: 'API Keys',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.API_KEYS_TAB] } }],
        index: 8,
      });
    }

    if (this.permissionService.isSysAdmin) {
      this.routeLinks.push(
        {
          label: 'Email Manager',
          link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.EMAIL_TAB] } }],
          index: 9,
        },
        {
          label: 'Global',
          link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.GLOBAL_TAB] } }],
          index: 10,
        },
        {
          label: 'Migrations',
          link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.MIGRATIONS_TAB] } }],
          index: 11,
        }
      );
    }

    // ZOO-10032: gated on TEMPLATE_READ_ALL alone until now, which only worked because every org's
    // hub_admin held that permission by accident. template:read_all is the CROSS-ORG read (it makes
    // the blueprint list endpoints skip their tenancy filter) and is now hub_systemadmin-only, so
    // gating the tab on it would take the Templates screen away from org admins managing their own
    // organisation's templates. template:read is the permission that actually backs both blueprint
    // GET endpoints ([RequirePermission(TemplateRead)]), so it is the correct gate; TEMPLATE_READ_ALL
    // stays in the check so a Catch sysadmin reaches the tab the same way.
    if (
      this.permissionService.hasPermission(PermissionConstants.TEMPLATE_READ) ||
      this.permissionService.hasPermission(PermissionConstants.TEMPLATE_READ_ALL)
    ) {
      this.routeLinks.push({
        label: 'Templates',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.BLUEPRINTS_TAB] } }],
        index: 9,
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.ATTACHMENT_READ)) {
      this.routeLinks.push({
        label: 'Attachments',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.ATTACHMENTS_TAB] } }],
        index: 8,
      });
    }

    if (this.permissionService.hasPermission(PermissionConstants.LOG_READ)) {
      this.routeLinks.push({
        label: 'Logs',
        link: ['/', 'dashboard', { outlets: { tabPanel: ['admin', CHATBOT_AUTO_CLOSE_ROUTES.LOGS_TAB] } }],
        index: 8,
      });
    }
  }
  ngOnInit(): void {
    this.dashboradHelper.setShowGraphPanel(false);

    const treeNodes: TreeNode[] = [];
    const categoryNodes = new Map<string, TreeNode>();
    for (const routeLink of this.routeLinks) {
      if (routeLink.category) {
        let categoryNode = categoryNodes.get(routeLink.category);
        if (!categoryNode) {
          categoryNode = { label: routeLink.category, expanded: true, children: [] };
          categoryNodes.set(routeLink.category, categoryNode);
          treeNodes.push(categoryNode);
        }
        categoryNode.children!.push({ label: routeLink.label, data: routeLink, leaf: true });
      } else {
        treeNodes.push({ label: routeLink.label, data: routeLink, leaf: true });
      }
    }
    this.dashboradHelper.setSettingsTreeNodes(treeNodes);
    this.dashboradHelper.setSettingsTabActive(true);

    this.router.events.subscribe((res: any) => {
      this.activeLinkIndex = this.routeLinks.indexOf(this.routeLinks.find((tab) => tab.link === '.' + this.router.url));
    });
  }

  ngOnDestroy(): void {
    this.dashboradHelper.setSettingsTabActive(false);
    this.dashboradHelper.setSettingsTreeNodes([]);
  }
}
